import _defineProperty from "/app/web/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import 'moment/locale/es';
import '../styles/antd.less';
import { config as fontAwesomeConfig, dom } from '@fortawesome/fontawesome-svg-core';
import { ConfigProvider, message } from 'antd';
import esES from 'antd/lib/locale/es_ES';
import { resources } from 'core/i18n/resources';
import { AppStoreContext, useAppStore } from 'core/modules/app/app.store';
import { AuthStoreContext, useAuthStore } from 'core/modules/auth/auth.store';
import { ClientsStoreContext, useClientsStore } from 'core/modules/clients/client.store';
import { CompaniesStoreContext, useCompaniesStore } from 'core/modules/companies/company.store';
import { LabelsStoreContext, useLabelsStore } from 'core/modules/labels/label.store';
import { MediaStoreContext, useMediaStore } from 'core/modules/media/media.store';
import { NotificationStoreContext, useNotificationStore } from 'core/modules/notifications/notification.store';
import { PreferencesStoreContext, usePreferencesStore } from 'core/modules/preferences/preferences.store';
import { MileageRatesStoreContext, useMileageRatesStore } from 'core/modules/rates/rates-mileage.store';
import { ReportRatesStoreContext, useReportRatesStore } from 'core/modules/rates/rates-report.store';
import { RouteStoreContext, useRouteStore } from 'core/modules/routes/route.store';
import { RoutePaginationStoreContext, useRoutePaginationStore } from 'core/modules/routes/ruotes-pagination.store';
import { SettingsStoreContext, useSettingsStore } from 'core/modules/settings/settings.store';
import { TasksStoreContext, useTasksStore } from 'core/modules/tasks/task.store';
import { TaskColumnStoreContext, useTaskColumnStore } from 'core/modules/tasks/task-column.store';
import { TaskSearchesStoreContext, useTaskSearchesStore } from 'core/modules/tasks/task-search.store';
import { UsersStoreContext, useUsersStore } from 'core/modules/users/user.store';
import { useWorkspacesStore, WorkspacesStoreContext } from 'core/modules/workspaces/workspace.store';
import { useZonesStore, ZonesStoreContext } from 'core/modules/zones/zone.store';
import { setLoggerReporter } from 'core/utils/logger';
import { Compose } from 'core/utils/store';
import i18n from 'i18next';
import moment from 'moment';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { AppLayout } from 'src/components/Layout/AppLayout';
import { config } from 'src/config';
import { useWebSocketsStore, WebSocketsStoreContext } from 'src/modules/websockets/websockets.store';
import { LocalStorageRepository } from 'src/utils/local-storage.repository';
import { loggerReporter } from 'src/utils/logger-reporter';
import useWindowSize, { WindowSizeContext } from 'src/utils/window';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
fontAwesomeConfig.autoAddCss = false;
i18n.use(initReactI18next).init({
  lng: 'es',
  fallbackLng: 'es',
  resources: resources
});
moment.locale('es');
var localStorageRepository = new LocalStorageRepository();
setLoggerReporter(loggerReporter);
export default function App(_ref) {
  var _auth$user;

  var Component = _ref.Component,
      pageProps = _ref.pageProps;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var router = useRouter();
  var windowSize = useWindowSize(); // Base stores

  var settings = useSettingsStore(config, localStorageRepository, function () {
    message.error(t('errors.sessionExpired'));
    auth.logout();
  });
  var media = useMediaStore(settings);
  var auth = useAuthStore(settings);
  var preferences = usePreferencesStore({
    auth: auth
  });
  var users = useUsersStore(auth, media);
  var zones = useZonesStore(auth);
  var labels = useLabelsStore(auth);
  var companies = useCompaniesStore(auth, media);
  var reportRates = useReportRatesStore(auth);
  var mileageRates = useMileageRatesStore(auth);
  var clients = useClientsStore(auth);
  var app = useAppStore({
    settings: settings,
    auth: auth,
    preferences: preferences,
    users: users,
    companies: companies,
    reportRates: reportRates,
    mileageRates: mileageRates,
    clients: clients,
    zones: zones,
    labels: labels,
    t: t
  }); // Task stores

  var taskSearches = useTaskSearchesStore(auth);
  var tasks = useTasksStore(auth, media, users, companies, clients, zones, labels);
  var taskColumns = useTaskColumnStore(tasks);
  var routes = useRouteStore(auth, tasks);
  var routesPagination = useRoutePaginationStore(routes.repository);
  var workspaces = useWorkspacesStore(app, taskColumns); // Notifications

  var notifications = useNotificationStore(auth, tasks);
  var websockets = useWebSocketsStore(auth, notifications);
  var isReady = auth.isReady,
      isAuthenticated = auth.isAuthenticated;
  var isForbidden = app.isReady && !isAuthenticated && !router.asPath.includes('/guest');
  useEffect(function () {
    if (app.isReady) {
      if (!isAuthenticated && !router.asPath.includes('/guest')) {
        router.replace('/guest/login');
      }

      if (isAuthenticated && router.pathname.includes('/guest') && !router.pathname.includes('/guest/recover/')) {
        router.replace('/');
      }
    }
  }, [app.isReady, isAuthenticated, router.asPath.includes('/guest')]);
  useEffect(function () {
    if (app.isReady && auth.user) {
      moment.locale(auth.user.language);
      i18n.changeLanguage(auth.user.language);
    }
  }, [app.isReady, (_auth$user = auth.user) === null || _auth$user === void 0 ? void 0 : _auth$user.language]);
  useEffect(function () {
    if (!isAuthenticated) {
      routesPagination.clear();
    }
  }, [isAuthenticated]);
  return /*#__PURE__*/_jsxs(Compose, {
    components: [[WindowSizeContext.Provider, {
      value: windowSize
    }], [MediaStoreContext.Provider, {
      value: media
    }], [SettingsStoreContext.Provider, {
      value: settings
    }], [AuthStoreContext.Provider, {
      value: auth
    }], [PreferencesStoreContext.Provider, {
      value: preferences
    }], [UsersStoreContext.Provider, {
      value: users
    }], [ZonesStoreContext.Provider, {
      value: zones
    }], [LabelsStoreContext.Provider, {
      value: labels
    }], [CompaniesStoreContext.Provider, {
      value: companies
    }], [ReportRatesStoreContext.Provider, {
      value: reportRates
    }], [MileageRatesStoreContext.Provider, {
      value: mileageRates
    }], [ClientsStoreContext.Provider, {
      value: clients
    }], [AppStoreContext.Provider, {
      value: app
    }], [TasksStoreContext.Provider, {
      value: tasks
    }], [TaskColumnStoreContext.Provider, {
      value: taskColumns
    }], [TaskSearchesStoreContext.Provider, {
      value: taskSearches
    }], [RouteStoreContext.Provider, {
      value: routes
    }], [RoutePaginationStoreContext.Provider, {
      value: routesPagination
    }], [WorkspacesStoreContext.Provider, {
      value: workspaces
    }], [NotificationStoreContext.Provider, {
      value: notifications
    }], [WebSocketsStoreContext.Provider, {
      value: websockets
    }]],
    children: [/*#__PURE__*/_jsxs(Head, {
      children: [/*#__PURE__*/_jsx("link", {
        rel: "preconnect",
        href: "https://fonts.googleapis.com"
      }), /*#__PURE__*/_jsx("link", {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "true"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "57x57",
        href: "/favicon/apple-icon-57x57.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "60x60",
        href: "/favicon/apple-icon-60x60.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "72x72",
        href: "/favicon/apple-icon-72x72.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "76x76",
        href: "/favicon/apple-icon-76x76.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "114x114",
        href: "/favicon/apple-icon-114x114.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "120x120",
        href: "/favicon/apple-icon-120x120.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "144x144",
        href: "/favicon/apple-icon-144x144.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "152x152",
        href: "/favicon/apple-icon-152x152.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/favicon/apple-icon-180x180.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "icon",
        type: "image/png",
        sizes: "192x192",
        href: "/favicon/android-icon-192x192.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon/favicon-32x32.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "icon",
        type: "image/png",
        sizes: "96x96",
        href: "/favicon/favicon-96x96.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon/favicon-16x16.png"
      }), /*#__PURE__*/_jsx("link", {
        rel: "manifest",
        href: "/manifest.json"
      }), /*#__PURE__*/_jsx("link", {
        href: "https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,600;1,300;1,700&display=swap",
        rel: "stylesheet"
      }), /*#__PURE__*/_jsx("style", {
        children: dom.css()
      }), /*#__PURE__*/_jsx("title", {
        children: "Jukuren"
      })]
    }), /*#__PURE__*/_jsx(ConfigProvider, {
      locale: esES,
      children: /*#__PURE__*/_jsx(AppLayout, {
        children: isReady && !isForbidden && /*#__PURE__*/_jsx(Component, _objectSpread({}, pageProps))
      })
    })]
  });
}